/**
 * Matching pairs.
 *
 * The generated editor cannot express this: it puts one control per schema field,
 * and a matching question is a *table*. Before this, the only field was a textarea
 * where a teacher typed "Cow, Milk" one pair per line, which is why the complaint
 * was "I do not see how to add options".
 *
 * So: a row per pair, left and right side by side, each able to carry text, a
 * picture, or both. Matching a picture to a word is the common case for young
 * children.
 *
 * The right column is shuffled on the printed page, not here - the teacher types
 * the pairs in their true order and the paper does the mixing, so the answer key
 * stays readable.
 */

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import type { Image, MatchingPair, Option } from '../../lib/api/client'
import { Button, IconButton, Input } from '../../components/ui'
import { Icon } from '../../components/icons'
import { PicturePicker } from './PicturePicker'

type Side = { text: string; image_id: string | null }
type Row = { left: Side; right: Side }

export function MatchingEditor({
  options,
  disabled,
  onChange,
}: {
  options: Option[]
  disabled?: boolean
  onChange: (pairs: MatchingPair[]) => void
}) {
  const [rows, setRows] = useState<Row[]>(() => toRows(options))

  /**
   * Re-sync from the server, but never throw away a row being filled in.
   *
   * An empty pair is not stored - it is a line the teacher has not written yet -
   * so a blank row exists only here. Replacing local state with the server's on
   * every refetch deleted it the instant it was added, which is why "Add pair"
   * looked like it did nothing at all.
   *
   * So: adopt the server's rows only when its content actually differs from what
   * is on screen, and keep any blank rows on the end either way.
   */
  useEffect(() => {
    setRows((current) => {
      const fromServer = toRows(options)
      const blanks = current.filter(isBlank)

      if (sameContent(current.filter((row) => !isBlank(row)), fromServer)) return current
      return [...fromServer, ...blanks]
    })
  }, [options])

  const pictures = useQuery({
    queryKey: ['pictures', 'all'],
    queryFn: () => api.pictures({ per_page: 100 }),
  })
  const library = pictures.data?.images ?? []

  const commit = (next: Row[]) => {
    setRows(next)
    onChange(next.map((row) => ({ left: row.left, right: row.right })))
  }

  const setSide = (index: number, side: 'left' | 'right', value: Partial<Side>) =>
    setRows(rows.map((row, i) => (i === index ? { ...row, [side]: { ...row[side], ...value } } : row)))

  return (
    <div className="tc-pairs">
      <div className="tc-pairs-head">
        <span className="tc-field-label">Pairs</span>
        <span className="tc-field-hint">
          Type each pair in its true order. The right column is mixed up on the printed page.
        </span>
      </div>

      <div className="tc-pairs-columns">
        <span className="tc-field-hint">Left</span>
        <span />
        <span className="tc-field-hint">Right</span>
        <span />
      </div>

      {rows.map((row, index) => (
        <div key={index} className="tc-pair-row">
          <SideEditor
            side={row.left}
            label={`Left ${index + 1}`}
            library={library}
            disabled={disabled}
            onEdit={(value) => setSide(index, 'left', value)}
            onCommit={(value) =>
              commit(rows.map((r, i) => (i === index ? { ...r, left: { ...r.left, ...value } } : r)))
            }
          />

          <span className="tc-pair-join" aria-hidden="true">
            —
          </span>

          <SideEditor
            side={row.right}
            label={`Right ${index + 1}`}
            library={library}
            disabled={disabled}
            onEdit={(value) => setSide(index, 'right', value)}
            onCommit={(value) =>
              commit(
                rows.map((r, i) => (i === index ? { ...r, right: { ...r.right, ...value } } : r)),
              )
            }
          />

          <IconButton
            label={`Remove pair ${index + 1}`}
            size="sm"
            variant="ghost"
            disabled={disabled}
            onClick={() => {
              const next = rows.filter((_, i) => i !== index)
              // Removing a row that was never stored changes nothing on the
              // server, so there is nothing to send.
              if (isBlank(row)) setRows(next)
              else commit(next)
            }}
          >
            <Icon.close size={14} />
          </IconButton>
        </div>
      ))}

      {/* Local only. There is nothing to save yet, and sending an empty pair would
          be refused - correctly - which is what made this button appear dead. */}
      <Button
        size="sm"
        icon={<Icon.plus size={14} />}
        disabled={disabled}
        onClick={() => setRows([...rows, blankRow()])}
      >
        Add pair
      </Button>
    </div>
  )
}

function SideEditor({
  side,
  label,
  library,
  disabled,
  onEdit,
  onCommit,
}: {
  side: Side
  label: string
  library: Image[]
  disabled?: boolean
  onEdit: (value: Partial<Side>) => void
  onCommit: (value: Partial<Side>) => void
}) {
  return (
    <div className="tc-pair-side">
      <Input
        aria-label={label}
        value={side.text}
        placeholder={label}
        disabled={disabled}
        onChange={(event) => onEdit({ text: event.target.value })}
        onBlur={() => onCommit({})}
      />

      <PicturePicker
        library={library}
        chosen={side.image_id}
        disabled={disabled}
        onPick={(imageId) => onCommit({ image_id: imageId })}
      />
    </div>
  )
}

/**
 * Options back into rows.
 *
 * They arrive two at a time - even position is a left item, the one after it is
 * its partner. A trailing odd option becomes a row with an empty right side so the
 * teacher can see and fix what the server is warning them about, rather than it
 * vanishing from the editor.
 */
function toRows(options: Option[]): Row[] {
  const rows: Row[] = []
  for (let index = 0; index < options.length; index += 2) {
    rows.push({
      left: toSide(options[index]),
      right: options[index + 1] ? toSide(options[index + 1]) : blankSide(),
    })
  }
  return rows.length ? rows : [blankRow(), blankRow()]
}

function toSide(option: Option): Side {
  return { text: option.text ?? '', image_id: option.image_id ?? null }
}

/** A row the teacher has started but not written in. Never stored. */
function isBlank(row: Row): boolean {
  return !hasContent(row.left) && !hasContent(row.right)
}

function hasContent(side: Side): boolean {
  return Boolean(side.text.trim() || side.image_id)
}

/** Whether two sets of rows say the same thing, ignoring how they were typed. */
function sameContent(a: Row[], b: Row[]): boolean {
  const key = (rows: Row[]) =>
    JSON.stringify(
      rows
        .filter((row) => !isBlank(row))
        .map((row) => [row.left.text.trim(), row.left.image_id, row.right.text.trim(), row.right.image_id]),
    )
  return key(a) === key(b)
}

function blankSide(): Side {
  return { text: '', image_id: null }
}

function blankRow(): Row {
  return { left: blankSide(), right: blankSide() }
}
